import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PageGen,
  PdfFont,
  PdfBrush,
  PdfColor,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { GLabel } from '../../pdf/glabel';
import '../../pdf/draw/primitives';
import '../../pdf/draw/label';
import '../../pdf/draw/cut';
import '../../pdf/draw/string';

export abstract class Cards implements ILayoutLabelGen, IItemCount {
  static margin = PdfUnit.FromMillimeters(5);

  abstract cardSize(): PdfSize;
  abstract labelSize(): PdfSize;

  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  protected abstract drawCard(
    page: PageGen,
    outerRect: PdfRect,
    innerRect: PdfRect,
    label: GLabel,
    font: PdfFont,
    isFront: boolean,
    cardIndex: number
  ): void;

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const cardSize = this.cardSize();
    const labelSize = this.labelSize();

    const cols = Math.floor((PdfSize.A4.height - 2 * Cards.margin) / cardSize.width);
    const rows = Math.floor((PdfSize.A4.width - 2 * Cards.margin) / cardSize.height);
    const itemsPerPage = cols * rows;

    const count = Math.min(opts.list.length, this.countMax());
    const sheetCount = Math.ceil(count / itemsPerPage);

    const leftTexts: string[][] = [];
    const rightTexts: string[][] = [];

    for (let i = 0; i < count; i++) {
      const item = opts.list[i];
      const leftText = item.left.getText() ?? '';
      const rightText = item.right.getText() ?? '';
      leftTexts.push(leftText ? [leftText] : []);
      rightTexts.push(rightText ? [rightText] : []);
    }

    const left = (PdfSize.A4.height - cols * cardSize.width) / 2;
    const top = (PdfSize.A4.width - rows * cardSize.height) / 2;

    for (let sheetIndex = 0; sheetIndex < sheetCount; sheetIndex++) {
      const pageFront = pdf.addPage(true);
      const pageBack = pdf.addPage(true);

      for (let y = 0; y <= rows; y++) {
        const lineY = top + y * cardSize.height;
        pageFront.drawLine(
          GUtil.cutPen,
          new PdfPoint(0, lineY),
          new PdfPoint(PdfSize.A4.height, lineY)
        );
      }
      for (let x = 0; x <= cols; x++) {
        const lineX = left + x * cardSize.width;
        pageFront.drawLine(
          GUtil.cutPen,
          new PdfPoint(lineX, 0),
          new PdfPoint(lineX, PdfSize.A4.width)
        );
      }

      const leftFont = pageFront.adjustFontMulti(leftTexts, GUtil.baseFont, labelSize);
      const rightFont = pageBack.adjustFontMulti(rightTexts, GUtil.baseFont, labelSize);

      const startIdx = sheetIndex * itemsPerPage;
      const endIdx = Math.min(startIdx + itemsPerPage, count);
      const sheetItemCount = endIdx - startIdx;

      const pageLeftTexts = leftTexts.slice(startIdx, endIdx);
      const pageRightTexts = rightTexts.slice(startIdx, endIdx);

      for (let x = 0; x < cols; x++) {
        for (let y = 0; y < rows; y++) {
          const index = y * cols + x;
          if (index >= sheetItemCount) continue;

          const frontCardX = left + x * cardSize.width;
          const cardY = top + y * cardSize.height;

          const frontOuterRect = new PdfRect(frontCardX, cardY, cardSize.width, cardSize.height);
          const frontInnerRect = new PdfRect(
            frontCardX + (cardSize.width - labelSize.width) / 2,
            cardY + (cardSize.height - labelSize.height) / 2,
            labelSize.width,
            labelSize.height
          );

          pageFront.drawRectangle(GUtil.cutPen, null, frontOuterRect);
          this.drawCard(
            pageFront,
            frontOuterRect,
            frontInnerRect,
            opts.list[startIdx + index].left,
            leftFont,
            true,
            startIdx + index
          );

          const backCardX = left + (cols - 1 - x) * cardSize.width;
          const backOuterRect = new PdfRect(backCardX, cardY, cardSize.width, cardSize.height);
          const backInnerRect = new PdfRect(
            backCardX + (cardSize.width - labelSize.width) / 2,
            cardY + (cardSize.height - labelSize.height) / 2,
            labelSize.width,
            labelSize.height
          );

          pageBack.drawRectangle(GUtil.cutPen, null, backOuterRect);
          this.drawCard(
            pageBack,
            backOuterRect,
            backInnerRect,
            opts.list[startIdx + index].right,
            rightFont,
            false,
            startIdx + index
          );
        }
      }
    }
  }
}

export class Karty__Zaklad extends Cards {
  cardSize(): PdfSize {
    const size = (PdfSize.A4.height - 2 * Cards.margin) / 5;
    return new PdfSize(size, (size * 7) / 5);
  }

  labelSize(): PdfSize {
    return this.cardSize();
  }

  protected drawCard(
    page: PageGen,
    outerRect: PdfRect,
    innerRect: PdfRect,
    label: GLabel,
    font: PdfFont,
    isFront: boolean,
    cardIndex: number
  ): void {
    page.drawLabel(label, { rect: innerRect, font });
  }
}

export class Karty__Cerny extends Cards {
  cardSize(): PdfSize {
    return new PdfSize(PdfUnit.FromMillimeters(90), PdfUnit.FromMillimeters(90));
  }

  labelSize(): PdfSize {
    const {margin} = Cards;
    return new PdfSize(this.cardSize().width - 2 * margin, this.cardSize().height - 2 * margin);
  }

  protected drawCard(
    page: PageGen,
    outerRect: PdfRect,
    innerRect: PdfRect,
    label: GLabel,
    font: PdfFont,
    isFront: boolean,
    cardIndex: number
  ): void {
    if (!isFront) {
      page.drawLabel(label, { rect: innerRect, font });
      return;
    }

    const {margin} = Cards;

    const topMarginRect = new PdfRect(outerRect.x, outerRect.y, outerRect.width, margin);
    const bottomMarginRect = new PdfRect(
      outerRect.x,
      outerRect.bottom - margin,
      outerRect.width,
      margin
    );
    const leftMarginRect = new PdfRect(
      outerRect.x,
      outerRect.y + margin,
      margin,
      outerRect.height - 2 * margin
    );
    const rightMarginRect = new PdfRect(
      outerRect.right - margin,
      outerRect.y + margin,
      margin,
      outerRect.height - 2 * margin
    );

    page.drawRectangle(null, PdfBrush.Black, topMarginRect);
    page.drawRectangle(null, PdfBrush.Black, bottomMarginRect);
    page.drawRectangle(null, PdfBrush.Black, leftMarginRect);
    page.drawRectangle(null, PdfBrush.Black, rightMarginRect);

    page.drawLabel(label, { rect: innerRect, font });
  }
}

const RAINBOW_COLORS = ['#FF9999', '#FFCC99', '#FFFF99', '#99FF99', '#99CCFF', '#CC99FF'];

export class Karty__Duha extends Cards {
  cardSize(): PdfSize {
    return new PdfSize(PdfUnit.FromMillimeters(65), PdfUnit.FromMillimeters(65));
  }

  labelSize(): PdfSize {
    const {margin} = Cards;
    return new PdfSize(this.cardSize().width - 2 * margin, this.cardSize().height - 2 * margin);
  }

  protected drawCard(
    page: PageGen,
    outerRect: PdfRect,
    innerRect: PdfRect,
    label: GLabel,
    font: PdfFont,
    isFront: boolean,
    cardIndex: number
  ): void {
    if (!isFront) {
      page.drawLabel(label, { rect: innerRect, font });
      return;
    }

    const {margin} = Cards;

    const topMarginRect = new PdfRect(outerRect.x, outerRect.y, outerRect.width, margin);
    const bottomMarginRect = new PdfRect(
      outerRect.x,
      outerRect.bottom - margin,
      outerRect.width,
      margin
    );
    const leftMarginRect = new PdfRect(
      outerRect.x,
      outerRect.y + margin,
      margin,
      outerRect.height - 2 * margin
    );
    const rightMarginRect = new PdfRect(
      outerRect.right - margin,
      outerRect.y + margin,
      margin,
      outerRect.height - 2 * margin
    );

    const color = new PdfBrush(PdfColor.fromHex(RAINBOW_COLORS[cardIndex % RAINBOW_COLORS.length]));

    page.drawRectangle(null, color, topMarginRect);
    page.drawRectangle(null, color, bottomMarginRect);
    page.drawRectangle(null, color, leftMarginRect);
    page.drawRectangle(null, color, rightMarginRect);

    page.drawLabel(label, { rect: innerRect, font });
  }
}
